import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, formatNumber, formatPercent, formatRangeLabel, getDatePreset } from '../utils/formatters';
import KPICard from '../components/KPICard';
import DateRangeFilter, { presetLabel } from '../components/DateRangeFilter';
import { downloadTableCsv } from '../utils/tableCsv';
import CustomerDetailModal from '../components/CustomerDetailModal';
import IconCsvButton from '../components/IconCsvButton';

const PAGE_SIZES = [10, 20, 50, 100, 'all'];

const TOP_CUSTOMER_COLUMNS = [
  { key: 'username',          label: 'Username',      align: 'left',  type: 'string' },
  { key: 'customer_name',     label: 'Customer Name', align: 'left',  type: 'string' },
  { key: 'total_orders',      label: 'Orders',        align: 'right', type: 'number' },
  { key: 'lifetime_value',    label: 'Spend',         align: 'right', type: 'number', fmt: (v) => formatCurrency(v) },
  { key: 'avg_order_value',   label: 'Avg Order',     align: 'right', type: 'number', fmt: (v) => formatCurrency(v) },
  { key: 'first_order_date',  label: 'First Order',   align: 'right', type: 'date',   fmt: (v) => formatDate(v) },
  { key: 'last_order_date',   label: 'Last Order',    align: 'right', type: 'date',   fmt: (v) => formatDate(v) },
  { key: 'notes',             label: 'Notes',         align: 'left',  type: 'string' },
];

const BREAK_CUSTOMER_COLUMNS = [
  { key: 'customer',     label: 'Username', align: 'left',  type: 'string' },
  { key: 'spend',        label: 'Spend',     align: 'right', type: 'number', fmt: (v) => formatCurrency(v) },
  { key: 'breaks',       label: 'Breaks',    align: 'right', type: 'number' },
  { key: 'spots',        label: 'Spots',     align: 'right', type: 'number' },
  { key: 'hits',         label: 'Hits',      align: 'right', type: 'number' },
  { key: 'hit_rate_pct', label: 'Hit %',     align: 'right', type: 'number', fmt: (v) => `${(v || 0).toFixed(1)}%` },
];

// Same shape as the break customers grid with First Buy Date slotted in beside
// the username — the date is the point of the table, so it reads before the
// numbers rather than after them.
const FIRST_TIME_BREAK_BUYER_COLUMNS = [
  BREAK_CUSTOMER_COLUMNS[0],
  { key: 'first_buy_date', label: 'First Buy Date', align: 'right', type: 'date', fmt: (v) => formatDate(v) },
  ...BREAK_CUSTOMER_COLUMNS.slice(1),
];

function compareRows(a, b, col, dir) {
  const av = a[col.key];
  const bv = b[col.key];
  if (col.type === 'string') {
    return (av || '').localeCompare(bv || '', undefined, { sensitivity: 'base' }) * dir;
  }
  if (col.type === 'date') {
    const at = av ? new Date(av).getTime() : 0;
    const bt = bv ? new Date(bv).getTime() : 0;
    return (at - bt) * dir;
  }
  return ((av ?? 0) - (bv ?? 0)) * dir;
}

function PageSizeSelector({ value, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
      Show:
      <select value={String(value)} onChange={e => onChange(e.target.value === 'all' ? 'all' : parseInt(e.target.value))} style={{ width: 'auto' }}>
        {PAGE_SIZES.map(s => <option key={s} value={String(s)}>{s === 'all' ? 'All' : s}</option>)}
      </select>
    </label>
  );
}

function ReportTable({ title, rows, columns, defaultSortKey, defaultSortDir, pageSize, onPageSizeChange, onRowClick, rowKey, emptyText, rangeLabel, filterLabel }) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [downloaded, setDownloaded] = useState(false);

  function toggleSort(col) {
    if (sortKey === col.key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col.key);
      setSortDir(col.type === 'string' ? 'asc' : 'desc');
    }
  }

  const sorted = useMemo(() => {
    const col = columns.find(c => c.key === sortKey) || columns[0];
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareRows(a, b, col, dir));
  }, [rows, columns, sortKey, sortDir]);

  const visible = pageSize === 'all' ? sorted : sorted.slice(0, pageSize);
  const colCount = columns.length + 1;

  // Always the whole sorted set, never the page on screen — the grid shows 10
  // of 400 by default and a download of the visible rows would be a quiet lie.
  // The header block matches the chart/table share export: title, period,
  // filters, sort, row count, then the pull stamp.
  function handleDownload() {
    const sortCol = columns.find(c => c.key === sortKey) || columns[0];
    downloadTableCsv(columns, sorted, {
      title,
      rangeLabel,
      notes: [
        filterLabel ? `Date filter: ${filterLabel}` : 'Date filter: Custom range',
        `Sorted by: ${sortCol.label} (${sortDir === 'asc' ? 'ascending' : 'descending'})`,
        `Rows: ${sorted.length} (every row, not only the ${pageSize === 'all' ? sorted.length : pageSize} on screen)`,
      ],
    });
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 1500);
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div className="card-title" style={{ margin: 0 }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {pageSize === 'all' ? `${sorted.length}` : `${visible.length} of ${sorted.length}`}
          </span>
          <PageSizeSelector value={pageSize} onChange={onPageSizeChange} />
          <IconCsvButton label={`Download ${title} as CSV`}
                         done={downloaded}
                         onClick={handleDownload}
                         disabled={sorted.length === 0} />
        </div>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'right', width: 40, color: 'var(--text-secondary)' }}>#</th>
              {columns.map(col => (
                <th key={col.key}
                    onClick={() => toggleSort(col)}
                    style={{ textAlign: col.align, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  {col.label}{' '}
                  {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={colCount} style={{ textAlign: 'center', color: 'var(--text-light)', padding: 24 }}>{emptyText}</td></tr>
            )}
            {visible.map((row, i) => (
              <tr key={rowKey ? rowKey(row, i) : i}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                {columns.map(col => (
                  <td key={col.key} style={{ textAlign: col.align }}>
                    {col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '--')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Repeat-purchase tiles. Lifetime and range rates sit side by side on purpose:
// they answer different questions and the range one moves with the date filter,
// so showing either alone invites reading it as the other.
function RepeatSummary({ repeat, rangeLabel }) {
  if (!repeat) return null;
  const { lifetime, in_range: inRange, new_vs_returning: split, excluded_orders: excluded } = repeat;

  return (
    <div className="kpi-section">
      <div className="kpi-section-title">Repeat Purchases</div>
      <div className="kpi-grid">
        <KPICard
          label="Repeat Rate (Lifetime)"
          value={formatPercent(lifetime.repeat_rate_pct)}
          secondary={`${formatNumber(lifetime.repeat_customers)} of ${formatNumber(lifetime.total_customers)}`}
          info={`Customers with 2+ purchases ever, across all time — this tile ignores the date filter. Averages ${lifetime.avg_orders_per_customer} orders per customer.`}
        />
        <KPICard
          label="Repeat Rate (Selected Range)"
          value={formatPercent(inRange.repeat_rate_pct)}
          secondary={`${formatNumber(inRange.repeat_customers)} of ${formatNumber(inRange.total_customers)}`}
          info={`Of customers who bought during ${rangeLabel}, the share who bought more than once inside that same window. Always lower than lifetime on a short range — a customer who bought in May and again this week counts as a repeat lifetime, but not here.`}
        />
        <KPICard
          label="Returning Customers"
          value={formatNumber(split.returning_customers)}
          secondary={`${formatNumber(split.new_customers)} new`}
          info={`Customers active during ${rangeLabel} whose first ever purchase came before the range started. The rest are first-timers acquired in the window.`}
        />
        <KPICard
          label="Returning Share of GMV"
          value={formatPercent(split.returning_gmv_share_pct)}
          secondary={formatCurrency(split.returning_gmv)}
          info={`Share of ${rangeLabel} GMV from returning customers (${formatPercent(split.returning_order_share_pct)} of orders). New customers brought ${formatCurrency(split.new_gmv)}.`}
        />
      </div>
      {excluded > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -12, marginBottom: 20 }}>
          Excludes {formatNumber(excluded)} paid {excluded === 1 ? 'order' : 'orders'} in this range with no buyer ID — those cannot be tied to a customer.
        </div>
      )}
    </div>
  );
}

export default function CustomerData() {
  const { user } = useAuth();
  const canEdit = user && ['admin', 'partner'].includes(user.role);
  const [dateRange, setDateRange] = useState(getDatePreset('last_30d'));
  // The name of the preset behind the range ("Last 30D"), so an export can say
  // which filter produced it. Null for a hand-picked custom range.
  const [rangeLabelName, setRangeLabelName] = useState(() => presetLabel('last_30d'));
  const [customers, setCustomers] = useState([]);
  const [breakCustomers, setBreakCustomers] = useState([]);
  const [repeat, setRepeat] = useState(null);
  const [firstTimeBuyers, setFirstTimeBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customersPageSize, setCustomersPageSize] = useState(10);
  const [breakPageSize, setBreakPageSize] = useState(10);
  const [firstTimePageSize, setFirstTimePageSize] = useState(10);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tc, tbc, ftb, rr] = await Promise.all([
        api.get('/api/reports/customer-data/top-customers', { params: dateRange }),
        api.get('/api/reports/customer-data/top-break-customers', { params: dateRange }),
        api.get('/api/reports/customer-data/first-time-break-buyers', { params: dateRange }),
        api.get('/api/reports/customer-data/repeat-rate', { params: dateRange }),
      ]);
      setCustomers(tc.data.customers || []);
      setBreakCustomers(tbc.data.top_break_customers || []);
      setFirstTimeBuyers(ftb.data.first_time_break_buyers || []);
      setRepeat(rr.data || null);
    } catch (err) {
      console.error('Customer Data fetch error:', err);
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function onNotesUpdated(buyerId, notes) {
    setCustomers(cs => cs.map(c => c.buyer_id === buyerId ? { ...c, notes } : c));
    setSelectedCustomer(prev => prev ? { ...prev, notes } : prev);
  }
  function onUsernameUpdated(buyerId, username) {
    setCustomers(cs => cs.map(c => c.buyer_id === buyerId ? { ...c, username } : c));
    setSelectedCustomer(prev => prev ? { ...prev, username } : prev);
  }

  const rangeLabel = formatRangeLabel(dateRange);

  return (
    <div>
      <h2 className="page-title" style={{ marginBottom: 20 }}>Customer Data</h2>
      <DateRangeFilter
        onChange={(range, meta) => { setDateRange(range); setRangeLabelName(meta?.label ?? null); }}
        initial="last_30d"
      />

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="spinner" /></div>}

      {!loading && (
        <>
          <RepeatSummary repeat={repeat} rangeLabel={rangeLabel} />

          <ReportTable
            title="Top Customers"
            rows={customers}
            columns={TOP_CUSTOMER_COLUMNS}
            defaultSortKey="lifetime_value"
            defaultSortDir="desc"
            pageSize={customersPageSize}
            onPageSizeChange={setCustomersPageSize}
            onRowClick={setSelectedCustomer}
            rowKey={(c) => c.buyer_id}
            emptyText="No customers in this date range."
            rangeLabel={rangeLabel}
            filterLabel={rangeLabelName}
          />

          <ReportTable
            title="Top Break Customers"
            rows={breakCustomers}
            columns={BREAK_CUSTOMER_COLUMNS}
            defaultSortKey="spend"
            defaultSortDir="desc"
            pageSize={breakPageSize}
            onPageSizeChange={setBreakPageSize}
            emptyText="No break customers in this date range."
            rangeLabel={rangeLabel}
            filterLabel={rangeLabelName}
          />

          <ReportTable
            title="First Time Buyers (Breaks Only)"
            rows={firstTimeBuyers}
            columns={FIRST_TIME_BREAK_BUYER_COLUMNS}
            defaultSortKey="spend"
            defaultSortDir="desc"
            pageSize={firstTimePageSize}
            onPageSizeChange={setFirstTimePageSize}
            emptyText="No first time break buyers in this date range."
            rangeLabel={rangeLabel}
            filterLabel={rangeLabelName}
          />
        </>
      )}

      {selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          canEdit={canEdit}
          onNotesUpdated={onNotesUpdated}
          onUsernameUpdated={onUsernameUpdated}
        />
      )}
    </div>
  );
}
